import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import useSessionTimeout from '../hooks/useSessionTimeout';
import LoginScreen from './LoginScreen';
import LandingPage from './LandingPage';
import HomePage from './HomePage';
import TrackPage from './TrackPage';
import MorePage from './MorePage';
import MainApp from './MainApp';
import NavigationBar from './NavigationBar';
import Dashboard from './Dashboard';

// Lazy load heavy components
const StocksApp = lazy(() => import('./StocksApp'));
const FitnessApp = lazy(() => import('./GymApp'));
const FoodApp = lazy(() => import('./FoodApp'));
const NotesApp = lazy(() => import('./NotesApp'));
const TasksApp = lazy(() => import('./TasksApp'));
const NewsApp = lazy(() => import('./NewsApp'));
const SleepApp = lazy(() => import('./SleepApp'));
const BooksApp = lazy(() => import('./BooksApp'));
const AskGroqApp = lazy(() => import('./AskGroqAppWithMCP'));
const AIChat = lazy(() => import('./AIChat'));
const AdminPanel = lazy(() => import('./AdminPanel'));

// Loading component
const LoadingFallback = () => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100%',
    flexDirection: 'column',
    gap: 2
  }}>
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);


// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} sx={{ color: '#0f172a' }} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/landing" replace />;
  }

  return children;
};

// Main App Layout component
const AppLayout = ({ children }) => {
  const { user } = useAuth();
  
  // Session timeout for 2 minutes of inactivity
  useSessionTimeout(2);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <NavigationBar />
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        height: 'calc(100vh - 64px)',
        background: '#f8fafc',
        zIndex: 1,
        position: 'relative',
      }}>
        <Suspense fallback={<LoadingFallback />}>
          {children}
        </Suspense>
      </Box>
    </Box>
  );
};

// Route definitions
const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/landing" element={<LandingPage />} />
        
        {/* Protected Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <AppLayout>
              <Navigate to="/home" replace />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/home" element={
          <ProtectedRoute>
            <AppLayout>
              <HomePage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/track" element={
          <ProtectedRoute>
            <AppLayout>
              <TrackPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/more" element={
          <ProtectedRoute>
            <AppLayout>
              <MorePage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/news" element={
          <ProtectedRoute>
            <AppLayout>
              <NewsApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/stocks" element={
          <ProtectedRoute>
            <AppLayout>
              <StocksApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/gym" element={
          <ProtectedRoute>
            <AppLayout>
              <FitnessApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/food" element={
          <ProtectedRoute>
            <AppLayout>
              <FoodApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/notes" element={
          <ProtectedRoute>
            <AppLayout>
              <NotesApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/sleep" element={
          <ProtectedRoute>
            <AppLayout>
              <SleepApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/books" element={
          <ProtectedRoute>
            <AppLayout>
              <BooksApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/tasks" element={
          <ProtectedRoute>
            <AppLayout>
              <TasksApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/askgroq" element={
          <ProtectedRoute>
            <AppLayout>
              <AskGroqApp />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/aichat" element={
          <ProtectedRoute>
            <AppLayout>
              <AIChat />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/admin" element={
          <ProtectedRoute>
            <AppLayout>
              <AdminPanel />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/news" replace />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
